const W = 'https://static.wixstatic.com/media/';

export const thermalCategories = {
  'thermal-acoustic': {
    slug: 'thermal-acoustic',
    path: '/products/thermal-acoustic',
    metaDescription: 'FOTRIC 2-in-1 thermal and acoustic imaging cameras: see heat and ultrasound on one screen for faster aviation leak, electrical and bearing diagnostics.',
    name: '2-in-1 Thermal and Acoustic Cameras',
    shortName: '2-in-1 AcouTherm Imagers',
    headline: 'One Camera. Thermal and Acoustic Together.',
    intro:
      'AcouTherm cameras pair a 640×480 radiometric thermal imager with a 162-microphone acoustic array in a single handheld body. Switch between Thermal, Acoustic, and MiX modes to see heat signatures and ultrasonic leaks or partial discharge on the same screen — ideal for electrical, pneumatic, and hydraulic troubleshooting on the flightline and in the shop.',
  },
  'thermal-cameras': {
    slug: 'thermal-cameras',
    path: '/products/thermal-cameras',
    name: 'Thermal Cameras',
    shortName: 'Thermal Cameras',
    headline: 'Professional Thermal Imaging for Aviation Maintenance.',
    intro:
      'From compact focus-free kits to 640×480 high-precision imagers, the thermal camera line covers wiring and connector hot spots, bleed-air and duct leaks, brake and wheel heat checks, de-ice verification, and avionics cooling — with TurboFocus® autofocus, IREdge® detail enhancement, and AnalyzIR® reporting software.',
  },
};

export const thermalProducts = [
  {
    slug: 'p-mix-acoutherm-camera',
    category: 'thermal-acoustic',
    name: 'P-MiX AcouTherm Camera',
    menuName: 'P-MiX AcouTherm Camera',
    models: 'P7MiX · P5MiX',
    tagline: 'Flagship 2-in-1 thermal + acoustic imager with a rotatable lens barrel and microphone array',
    description:
      'The P-MiX AcouTherm Camera combines a 640×480 thermal imager with a 162-channel MEMS acoustic array in one professional body. Three adjustable modes — IR, AC, and MiX — let inspectors view heat distribution and ultrasonic sound sources simultaneously on the 5-inch display, so electrical hot spots, partial discharge, gas leaks, and abnormal vibration are confirmed from multiple perspectives in a single pass. A rotatable infrared lens barrel and microphone array reach into tight corners and overhead spaces without compromising image quality.',
    images: [
      `${W}571718_9c0a25051f984033b3911299b3d048e1~mv2.png/v1/fill/w_720,h_752,al_c,q_85,enc_auto/mix-back.png`,
      `${W}571718_d57ecd5a204f4effa5ec4b1b8c408401~mv2.png/v1/fill/w_720,h_720,al_c,q_85,enc_auto/mix-front.png`,
      `${W}571718_c0f77da72bbb4ef9b7964eba9b699d05~mv2.png/v1/fill/w_720,h_405,al_c,q_85,enc_auto/mix.png`,
    ],
    highlights: ['640×480 IR', '162 MEMS mics', 'IR · AC · MiX modes', '1.6 kg'],
    features: [
      { title: 'Three Imaging Modes', description: 'Switch between Thermal (IR), Acoustic (AC), and MiX — thermal distribution and acoustic patterns rendered together on one screen for cross-verified diagnosis.' },
      { title: '640×480 Thermal with Selectable Lenses', description: 'Single-view 46°, 25°, 12°, 7° and dual-view 25°&12° / 25°&7° lens options with <30 mK sensitivity and TurboFocus® smart focusing.' },
      { title: '162-Microphone Acoustic Array', description: 'AI-assisted partial discharge diagnosis (surface, floating, corona) and leakage evaluation with leak level, leak rate, and leak cost estimates.' },
      { title: 'Rotatable Lens Barrel and Array', description: 'Rotate the infrared lens and microphone array to capture hard-to-reach equipment while keeping the display readable and the technician safe.' },
      { title: 'NaviPdM® AI Inspection Assistant', description: 'Automated target identification, Delta-T diagnostics, structured routing inspections, trend analysis, and reporting.' },
      { title: 'AnalyzIR® Software', description: 'Analyze thermal, acoustic, and combined images and video on the PC with report generation.' },
    ],
    specifications: [
      { label: 'IR Resolution', value: '640 × 480' },
      { label: 'Thermal Sensitivity (NETD)', value: '<30 mK @ 30 °C' },
      { label: 'Thermal FOV (standard lens)', value: '25° × 19°' },
      { label: 'Focus', value: 'TurboFocus® (contrast, laser-assisted, continuous, touch AF); manual' },
      { label: 'Microphone Channels', value: '162 MEMS digital microphones' },
      { label: 'Acoustic FOV', value: '66° × 52°' },
      { label: 'Acoustic Working Distance', value: '0.3 – 100 m (1 – 328 ft)' },
      { label: 'Temperature Range', value: '-20 to 120 °C; 0 to 650 °C; intelligent range' },
      { label: 'Measurement Accuracy', value: '±1 °C or ±1 % (0–100 °C); ±2 °C or ±2 % elsewhere' },
      { label: 'Display', value: '5" 1280×720 IPS touchscreen, Gorilla glass' },
      { label: 'Digital Camera', value: '13 MP' },
      { label: 'Storage', value: 'Hot-swappable SD card, up to 1 TB' },
      { label: 'Weight', value: '1.6 kg with battery, without lens' },
    ],
    applications: [
      'Electrical hot-spot and partial-discharge inspection in one pass',
      'Compressed air, nitrogen, and pneumatic system leak quantification',
      'Hydraulic and fuel system fault confirmation with thermal + acoustic cross-check',
      'Abnormal noise and vibration on rotating equipment',
      'Hard-to-reach wheel-well, pylon, and overhead inspections',
    ],
  },
  {
    slug: 'v-mix-acoutherm-camera',
    category: 'thermal-acoustic',
    name: 'V-MiX AcouTherm Camera',
    menuName: 'V-MiX AcouTherm Camera',
    models: 'V7MiX · V5MiX',
    tagline: 'Compact handheld 2-in-1 imager under 1.5 kg for leak detection and partial discharge diagnosis',
    description:
      'The V-MiX AcouTherm Camera brings professional thermal and acoustic imaging into a compact two-in-one handheld weighing less than 1.5 kg. A 640×480 infrared sensor, 162 MEMS digital microphones, and a 13 MP industrial digital camera deliver detailed dual imaging on a 5-inch IPS touchscreen, making compressed-gas leak detection, SF6 detection, and electrical safety inspections fast and repeatable.',
    images: [
      `${W}571718_fc929bf399b04939949d24f5293c3eee~mv2.png/v1/fill/w_707,h_555,al_c,q_85,enc_auto/vmix.png`,
      `${W}571718_1003ff0ee752415da1e632ef39a7d272~mv2.png/v1/crop/x_699,y_0,w_2053,h_2251/fill/w_720,h_789,al_c,q_85,enc_auto/vmix-2.png`,
    ],
    highlights: ['640×480 IR', '162 MEMS mics', '13 MP camera', '<1.5 kg'],
    features: [
      { title: 'Two-in-One Design', description: 'Professional thermal and acoustic imaging capabilities in one body weighing less than 1.5 kg.' },
      { title: '640×480 Infrared Sensor', description: 'Impeccable image quality, accurate measurement, and stability with TurboFocus® autofocus.' },
      { title: '162 MEMS Digital Microphones', description: 'Ultra-high sensitivity acoustic array with 0.3 – 100 m working distance for leaks and partial discharge.' },
      { title: '13 MP Industrial Digital Camera', description: 'Superior visible-light detail in industrial environments for picture-in-picture and blend modes.' },
      { title: '5-inch IPS Touchscreen', description: '1280×720 display with Gorilla anti-explosion glass for clarity and durability on the flightline.' },
      { title: 'NaviPdM® and AnalyzIR®', description: 'AI-powered inspection routing and PC analysis software for reporting and trend tracking.' },
    ],
    specifications: [
      { label: 'IR Resolution', value: '640 × 480' },
      { label: 'Thermal Sensitivity (NETD)', value: '<30 mK @ 30 °C' },
      { label: 'Thermal FOV', value: '25° × 19°' },
      { label: 'Focus', value: 'TurboFocus® (contrast, laser-assisted, continuous, touch AF); manual' },
      { label: 'Microphone Channels', value: '162 MEMS digital microphones' },
      { label: 'Acoustic FOV', value: '66° × 52°' },
      { label: 'Acoustic Working Distance', value: '0.3 – 100 m' },
      { label: 'Temperature Range', value: '-20 to 120 °C; 0 to 650 °C; intelligent range' },
      { label: 'Measurement Accuracy', value: '±2 °C or ±2 %, whichever is greater' },
      { label: 'Display', value: '5" 1280×720 LCD touchscreen, Gorilla glass' },
      { label: 'Image Modes', value: 'Thermal / Digital / PIP / T-DEF® blend / High sensitivity; Acoustic Single / Multi / Hologram' },
      { label: 'Storage', value: 'Hot-swappable SD card, up to 1 TB' },
      { label: 'Weight', value: 'Less than 1.5 kg' },
    ],
    applications: [
      'Compressed gas and pneumatic leak detection',
      'SF6 and specialty gas detection',
      'Electrical equipment safety and partial-discharge inspection',
      'Predictive maintenance walk-downs where one tool must do both jobs',
    ],
  },
  {
    slug: 'ti3-ti4',
    category: 'thermal-cameras',
    name: 'Ti3 and Ti4 SharpView Thermal Cameras',
    menuName: 'Ti3 and Ti4',
    models: 'Ti3 · Ti4',
    tagline: 'Professional thermal imaging made more accessible — TurboFocus® autofocus at an entry price',
    description:
      'The Ti3 and Ti4 SharpView thermal cameras bring professional-grade features to everyday inspection. The Ti4 delivers 320×240 IR resolution with <40 mK sensitivity; the Ti3 offers 160×120 with <50 mK. Both include the TurboFocus® intelligent focus system, IREdge® contour enhancement, T-TWB® temperature visualization, laser-assisted area and length measurement, and on-device analysis on a 5-inch touchscreen.',
    images: [
      `${W}0fa391_611c4719f7834306ba128558d3092f88f000.jpg/v1/fill/w_900,h_600,al_c,q_85,enc_auto/ti3-ti4.jpg`,
      `${W}0fa391_a6916949c10e46bdb15267e39d41010b~mv2.jpg/v1/fill/w_900,h_600,al_c,q_85,enc_auto/ti-thermal-1.jpg`,
      `${W}0fa391_cf9a95159fdc40e489d8d1c1813554d9~mv2.jpg/v1/fill/w_900,h_600,al_c,q_85,enc_auto/ti-thermal-2.jpg`,
    ],
    highlights: ['320×240 / 160×120', '<40 mK / <50 mK', 'TurboFocus®', '8 MP camera'],
    features: [
      { title: 'TurboFocus® Intelligent Focus', description: 'Continuous, laser-assisted, thermal-contrast, and touch autofocus plus manual — sharp images without fiddling.' },
      { title: 'IREdge® Detail Enhancement', description: 'Contour enhancement makes connectors, terminals, and small components stand out in the thermal image.' },
      { title: 'T-TWB® Visualization', description: 'Temperature visual representation normalization for consistent, readable palettes across scenes.' },
      { title: 'Laser-Assisted Measurement', description: 'Measure area and length on-device with the built-in laser for documented inspections.' },
      { title: 'On-device Analysis', description: 'Spot, line, and area markers with immediate readouts; export to AnalyzIR® for reporting.' },
      { title: 'Robust Design', description: 'Built for daily use in maintenance environments with a 5-inch 1280×720 touchscreen.' },
    ],
    specifications: [
      { label: 'IR Resolution', value: 'Ti4: 320 × 240 · Ti3: 160 × 120' },
      { label: 'Thermal Sensitivity (NETD)', value: 'Ti4: <40 mK · Ti3: <50 mK @ 30 °C' },
      { label: 'Frame Rate', value: '30 Hz' },
      { label: 'Field of View', value: 'Ti4: 44° × 33° · Ti3: 25° × 19°' },
      { label: 'Focus', value: 'TurboFocus® system; manual' },
      { label: 'Temperature Range', value: '-20 to 120 °C; 0 to 650 °C; intelligent range' },
      { label: 'Measurement Accuracy', value: '±2 °C or ±2 %, whichever is greater' },
      { label: 'Display', value: '5" touchscreen, 1280 × 720' },
      { label: 'Digital Camera', value: '8 MP industrial grade' },
      { label: 'Palettes', value: '10' },
      { label: 'PC Software', value: 'AnalyzIR®' },
    ],
    applications: [
      'Electrical connector and wiring hot-spot checks',
      'Avionics bay and cooling airflow verification',
      'Brake, wheel, and bearing temperature surveys',
      'Facility and hangar electrical panel inspection',
    ],
  },
  {
    slug: 'tk5-kit',
    category: 'thermal-cameras',
    name: 'TK5 Kit Thermal Imaging Camera',
    menuName: 'TK5 Kit',
    models: 'TK5Kit',
    tagline: 'Real sensor, focus-free 320×240 handheld kit with case, spare battery, and holster',
    description:
      'The TK5 Kit is a compact industrial-grade handheld thermal camera built around a real 320×240 ceramic-packaged uncooled detector with 40 mK sensitivity. Focus-free optics, a 49°×37° wide field of view, 30 Hz refresh, T-DEF® blend mode, and 20 spot / 10 line / 10 area measurement tools make it a fast walk-around imager. The kit adds a durable hard case, soft bag, two lithium batteries, a wearable tool holster, and extended warranty service.',
    images: [
      `${W}0fa391_38b2c4ea42894d98835375b0ac033b1bf000.jpg/v1/fill/w_900,h_600,al_c,q_85,enc_auto/tk5-kit.jpg`,
      `${W}0fa391_b887f09356854921bb833f88d20c1cb6~mv2.png/v1/fill/w_900,h_600,al_c,q_85,enc_auto/tk5-thermal-1.png`,
      `${W}0fa391_0e8c92fb62714a32851c73fd9b3bb26d~mv2.jpg/v1/fill/w_900,h_600,al_c,q_85,enc_auto/tk5-thermal-2.jpg`,
    ],
    highlights: ['320×240 IR', '40 mK', 'Focus free', '-20 to 450 °C'],
    features: [
      { title: 'Real 320×240 Sensor', description: 'Ceramic-packaged uncooled FPA detector with 40 mK sensitivity — genuine resolution, not interpolated.' },
      { title: 'Focus-Free Optics', description: 'Point and shoot from 0.4 m with a wide 49° × 37° field of view.' },
      { title: 'T-DEF® Blend Mode', description: 'Blend thermal and visible images with 0 – 100 % transparency to locate the exact component.' },
      { title: 'Versatile Measurement Tools', description: '20 spot, 10 line, and 10 area markers with parameter compensation and color alarms.' },
      { title: 'High Refresh Rate', description: '30 Hz imaging for smooth scanning of moving or rotating equipment.' },
      { title: 'Complete Kit', description: 'Hard carrying case, soft bag, two lithium batteries, wearable tool holster, and extended warranty.' },
    ],
    specifications: [
      { label: 'IR Resolution', value: '320 × 240' },
      { label: 'Detector', value: 'Ceramic-packaged uncooled FPA' },
      { label: 'Thermal Sensitivity (NETD)', value: '40 mK @ 30 °C' },
      { label: 'Field of View', value: '49° × 37°' },
      { label: 'Spatial Resolution (IFOV)', value: '2.67 mrad' },
      { label: 'Minimum Imaging Distance', value: '0.4 m' },
      { label: 'Focus', value: 'Focus free' },
      { label: 'Temperature Range', value: '-20 to 450 °C (-4 to 842 °F)' },
      { label: 'Accuracy', value: '±2 °C or ±2 %, whichever is greater' },
      { label: 'Frame Rate', value: '30 Hz' },
      { label: 'Measurement Markers', value: '20 spot · 10 line · 10 area' },
      { label: 'Image Modes', value: 'Thermal, digital, picture-in-picture, T-DEF® blend' },
      { label: 'Kit Contents', value: 'Hard case, soft bag, 2 lithium batteries, tool holster, extended warranty' },
    ],
    applications: [
      'Quick electrical and connector heat surveys on the ramp',
      'Cabin and cargo-bay duct and insulation checks',
      'Motor, pump, and bearing temperature trending',
      'Toolbox-ready thermal imaging for line technicians',
    ],
  },
  {
    slug: 'tk6-kit',
    category: 'thermal-cameras',
    name: 'TK6 Kit Thermal Imaging Camera',
    menuName: 'TK6 Kit',
    models: 'TK6Kit',
    tagline: 'Real 384×288 sensor with manual focus and 0.15 m ultra-close imaging, delivered as a full kit',
    description:
      'The TK6 Kit steps up to a real 384×288 ceramic-packaged uncooled detector with 40 mK sensitivity and a 25°×19° lens with manual focus down to 0.15 m — ideal for small connectors, circuit cards, and tight assemblies. It keeps the T-DEF® blend mode, 30 Hz refresh, and full measurement toolset of the TK line, and ships with a hard case, soft bag, two lithium batteries, a wearable tool holster, and extended warranty service.',
    images: [
      `${W}571718_16fb189b068d43348cd0e02a10266d34~mv2.png/v1/fill/w_1200,h_749,al_c,q_85,enc_auto/tk6kit.png`,
      `${W}0fa391_0d6786fc2d4941fa8f61205b50d2ed5e~mv2.jpg/v1/fill/w_800,h_600,al_c,q_85,enc_auto/tk6-1.jpg`,
      `${W}0fa391_9156a730bec74a5183e83e02a4559b82~mv2.jpg/v1/fill/w_800,h_600,al_c,q_85,enc_auto/tk6-2.jpg`,
    ],
    highlights: ['384×288 IR', '40 mK', 'Manual focus', '0.15 m close-up'],
    features: [
      { title: 'Real 384×288 Sensor', description: 'Higher-resolution ceramic-packaged uncooled FPA detector with 40 mK sensitivity.' },
      { title: 'Manual Focus + Ultra-Close Imaging', description: 'Focus down to 0.15 m with 1.14 mrad IFOV to resolve small components and terminals.' },
      { title: 'T-DEF® Blend Mode', description: 'Thermal and visible-light blend with 0 – 100 % transparency for exact fault location.' },
      { title: 'Versatile Measurement Tools', description: '20 spot, 10 line, and 10 area markers, parameter compensation, and color alarms.' },
      { title: 'High Refresh Rate', description: '30 Hz imaging for smooth scanning.' },
      { title: 'Complete Kit', description: 'Hard carrying case, soft bag, two lithium batteries, wearable tool holster, and extended warranty.' },
    ],
    specifications: [
      { label: 'IR Resolution', value: '384 × 288' },
      { label: 'Detector', value: 'Ceramic-packaged uncooled FPA' },
      { label: 'Thermal Sensitivity (NETD)', value: '40 mK @ 30 °C' },
      { label: 'Field of View', value: '25° × 19°' },
      { label: 'Spatial Resolution (IFOV)', value: '1.14 mrad' },
      { label: 'Minimum Imaging Distance', value: '0.15 m' },
      { label: 'Focus', value: 'Manual focus' },
      { label: 'Temperature Range', value: '-20 to 550 °C (-4 to 1022 °F)' },
      { label: 'Accuracy', value: '±2 °C or ±2 %, whichever is greater' },
      { label: 'Frame Rate', value: '30 Hz' },
      { label: 'Measurement Markers', value: '20 spot · 10 line · 10 area' },
      { label: 'Image Modes', value: 'Thermal, digital, picture-in-picture, T-DEF® blend' },
      { label: 'Kit Contents', value: 'Hard case, soft bag, 2 lithium batteries, tool holster, extended warranty' },
    ],
    applications: [
      'Circuit-card and connector-pin level inspection',
      'Avionics and power distribution troubleshooting',
      'Higher-temperature exhaust and bleed-air component checks',
      'Detailed documentation of repairs before return to service',
    ],
  },
  {
    slug: 'ti-series',
    category: 'thermal-cameras',
    metaDescription: 'FOTRIC Ti Series SharpView thermal cameras for aviation maintenance: high-resolution IR imaging, smart focus and reporting for airframe and powerplant checks.',
    name: 'Ti Series Advanced SharpView Thermal Cameras',
    menuName: 'Ti Series',
    models: 'Ti7 · Ti5',
    tagline: 'High-precision 640×480 (Ti7) and 384×288 (Ti5) imagers with TurboFocus® and 1.0 kg body',
    description:
      'The Ti Series Advanced SharpView cameras deliver impeccable imaging for demanding inspections. The Ti7 provides 640×480 resolution with <30 mK sensitivity; the Ti5 offers 384×288. Both feature a wide 44°×33° standard lens, TurboFocus® multi-mode autofocus, up to 12 spot / 3 line / 12 area markers, an 8 MP digital camera, a 5-inch 1280×720 touchscreen, and a 3500 mAh battery — in a 1.0 kg body.',
    images: [
      `${W}571718_2a6d50e3ca644e8d942a34b8fd1cf600~mv2.png/v1/fill/w_720,h_1051,al_c,q_85,enc_auto/ti-series.png`,
      `${W}571718_0cd4a06529b247fa8a32f6e4c400695d~mv2.jpg/v1/fill/w_768,h_567,al_c,q_85,enc_auto/twb-off.jpg`,
      `${W}571718_124869d52bd742928902dc1ec6a53963~mv2.jpg/v1/fill/w_768,h_570,al_c,q_85,enc_auto/twb-on.jpg`,
    ],
    highlights: ['640×480 / 384×288', '<30 mK', 'TurboFocus®', '1.0 kg'],
    features: [
      { title: 'Impeccable Imaging', description: '640×480 (Ti7) or 384×288 (Ti5) with <30 mK sensitivity and a wide 44° × 33° lens.' },
      { title: 'TurboFocus® Autofocus', description: 'Continuous, laser-assisted, thermal-contrast, and touch AF plus manual for sharp results every time.' },
      { title: 'Powerful On-Device Analysis', description: 'Up to 12 spot, 3 line, and 12 area markers for swift decision-making in the field.' },
      { title: 'Robust and Versatile', description: '1.0 kg body, 5-inch 1280×720 touchscreen, 8 MP digital camera, micro-SD up to 1 TB.' },
      { title: 'IREdge® and T-TWB®', description: 'Contour enhancement and temperature visualization normalization for readable, consistent images.' },
      { title: 'AnalyzIR® Software', description: 'Professional PC analysis and report generation across the thermal product line.' },
    ],
    specifications: [
      { label: 'IR Resolution', value: 'Ti7: 640 × 480 · Ti5: 384 × 288' },
      { label: 'Thermal Sensitivity (NETD)', value: '<30 mK @ 30 °C' },
      { label: 'Field of View', value: '44° × 33°' },
      { label: 'Focus', value: 'TurboFocus® (continuous, laser-assisted, contrast, touch AF); manual' },
      { label: 'Temperature Range', value: '-20 to 120 °C; 0 to 650 °C; intelligent range' },
      { label: 'Measurement Markers', value: '12 spot · 3 line · 12 area' },
      { label: 'Measurement Accuracy', value: '±2 °C or ±2 %, whichever is greater' },
      { label: 'Digital Camera', value: '8 MP industrial grade' },
      { label: 'Display', value: '5" touchscreen, 1280 × 720' },
      { label: 'Storage', value: 'Micro SD 64 GB, expandable to 1 TB' },
      { label: 'Battery', value: 'Rechargeable Li-ion 7.4 V, 3500 mAh' },
      { label: 'Weight', value: '1.0 kg (lens not included)' },
    ],
    applications: [
      'Engine nacelle, pylon, and APU thermal surveys',
      'Electrical power distribution and generator inspection',
      'De-icing and anti-ice system verification',
      'Composite and bonded-structure moisture and delamination screening',
    ],
  },
  {
    slug: 'v-series',
    category: 'thermal-cameras',
    name: 'V-Series Professional Thermal Camera',
    menuName: 'V-Series',
    models: 'V7 · V5',
    tagline: 'Professional 640×480 imager with 30 mK sensitivity and a -20 to 1550 °C range',
    description:
      'The V-Series professional thermal camera is built to improve maintenance efficiency in electric, oil and gas, and manufacturing environments — and translates directly to aviation powerplant and systems work. A 640×480 detector with 30 mK sensitivity, 0.19 mrad spatial resolution with optional lenses, a wide -20 to 1550 °C measurement range, a 13 MP visible camera, 16 spot / 8 area / 2 line markers, 16 palettes, and NaviPdM® AI routing make it the top of the thermal line.',
    images: [
      `${W}571718_1e5eaca495d64cdb83fb6d89cd616141~mv2.jpg/v1/fill/w_800,h_800,al_c,q_85,enc_auto/v-series.jpg`,
      `${W}571718_d14ec6062afa4ee0aa110e0578089060~mv2.jpg/v1/fill/w_588,h_442,al_c,q_85,enc_auto/v-twb-off.jpg`,
      `${W}571718_dd47cb22b7a84c0389d63a6062bb4902~mv2.jpg/v1/fill/w_592,h_442,al_c,q_85,enc_auto/v-twb-on.jpg`,
    ],
    highlights: ['640×480 IR', '30 mK', '-20 to 1550 °C', '13 MP camera'],
    features: [
      { title: 'Extraordinary Performance', description: '640 × 480 resolution, 30 mK sensitivity, and 0.19 mrad spatial resolution for small, distant targets.' },
      { title: 'Wide Temperature Range', description: 'Three ranges spanning -20 to 1550 °C cover exhaust, combustion, and high-temperature process checks.' },
      { title: 'Cutting-Edge Image Algorithms', description: 'IREdge®, T-TWB®, and blend modes deliver clear, measurable thermal images.' },
      { title: 'NaviPdM® AI Inspection', description: 'Route-based inspections, automatic target recognition, Delta-T diagnostics, and trend reporting.' },
      { title: 'Comprehensive Analysis Options', description: '16 spot markers, 8 area (rectangle or circle), 2 lines, and 16 standard palettes.' },
      { title: 'TurboFocus® + 13 MP Camera', description: 'Continuous, laser-assisted, and contrast autofocus paired with a 13 MP industrial visible camera.' },
    ],
    specifications: [
      { label: 'IR Resolution', value: '640 × 480' },
      { label: 'Thermal Sensitivity (NETD)', value: '30 mK @ 30 °C' },
      { label: 'Field of View (standard lens)', value: '25° × 19°' },
      { label: 'Spatial Resolution', value: 'Down to 0.19 mrad with optional lenses' },
      { label: 'Focus', value: 'TurboFocus® (continuous, laser-assisted, contrast AF); manual' },
      { label: 'Temperature Range', value: '-20 to 120 °C; 0 to 650 °C; 300 to 1550 °C' },
      { label: 'Accuracy', value: '±2 °C or ±2 %, whichever is greater' },
      { label: 'Measurement Markers', value: '16 spot · 8 area · 2 line' },
      { label: 'Digital Camera', value: '13 MP industrial grade' },
      { label: 'Storage', value: 'Micro SD 128 GB, expandable to 1 TB' },
      { label: 'Palettes', value: '16 standard palettes' },
    ],
    applications: [
      'Exhaust, combustor, and high-temperature powerplant inspection',
      'Electrical distribution and generator troubleshooting',
      'Oil, fuel, and hydraulic system heat mapping',
      'Route-based predictive maintenance programs with NaviPdM®',
    ],
  },
];

export const thermalProductsByCategory = (categorySlug) =>
  thermalProducts.filter((p) => p.category === categorySlug);

export const thermalProductBySlug = (categorySlug, slug) =>
  thermalProducts.find((p) => p.category === categorySlug && p.slug === slug) || null;

export const thermalProductPath = (p) => `${thermalCategories[p.category].path}/${p.slug}`;

export const THERMAL_COMPARE_GROUPS = [
  {
    label: 'Imaging',
    rows: [
      { key: 'resolution', label: 'IR resolution', match: ['IR Resolution'] },
      { key: 'netd', label: 'Thermal sensitivity (NETD)', match: ['Thermal Sensitivity'] },
      { key: 'fov', label: 'Field of view', match: ['Field of View', 'Thermal FOV'] },
      { key: 'ifov', label: 'Spatial resolution', match: ['Spatial Resolution'] },
      { key: 'focus', label: 'Focus', match: ['Focus'] },
      { key: 'frame', label: 'Frame rate', match: ['Frame Rate'] },
    ],
  },
  {
    label: 'Measurement',
    rows: [
      { key: 'range', label: 'Temperature range', match: ['Temperature Range'] },
      { key: 'accuracy', label: 'Accuracy', match: ['Accuracy', 'Measurement Accuracy'] },
      { key: 'markers', label: 'Measurement markers', match: ['Measurement Markers'] },
      { key: 'palettes', label: 'Palettes', match: ['Palettes'] },
    ],
  },
  {
    label: 'Camera & body',
    rows: [
      { key: 'camera', label: 'Digital camera', match: ['Digital Camera'] },
      { key: 'display', label: 'Display', match: ['Display'] },
      { key: 'storage', label: 'Storage', match: ['Storage'] },
      { key: 'battery', label: 'Battery', match: ['Battery'] },
      { key: 'weight', label: 'Weight', match: ['Weight'] },
      { key: 'kit', label: 'Kit contents', match: ['Kit Contents'] },
    ],
  },
];

export const getThermalCell = (product, row) => {
  const spec = product.specifications.find((s) => row.match.some((m) => s.label.toLowerCase().startsWith(m.toLowerCase())));
  return spec ? spec.value : '';
};
